import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, MailCheck } from "lucide-react";

export function AuthModal({ open, onOpenChange, onSuccess }) {
    const { login, register, forgotPassword } = useAuth();
    const [mode, setMode] = useState("login"); // login | register | forgot
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [accept, setAccept] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const reset = (m) => { setMode(m); setErr(""); setSent(false); };

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            if (mode === "login") { await login(form.email, form.password); toast.success("Welcome back"); onOpenChange(false); onSuccess?.(); }
            else if (mode === "register") {
                if (!accept) { setErr("Please accept the Terms of Use and Privacy Notice."); setBusy(false); return; }
                await register(form.name, form.email, form.password, true); toast.success("Account created"); onOpenChange(false); onSuccess?.();
            } else {
                await forgotPassword(form.email); setSent(true);
            }
        } catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const titles = { login: "Sign in", register: "Create your account", forgot: "Reset your password" };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="auth-modal">
                <DialogHeader>
                    <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-navy/10"><Lock className="h-5 w-5 text-navy" /></div>
                    <DialogTitle className="text-center font-heading text-2xl text-navy">{titles[mode]}</DialogTitle>
                    <p className="text-center text-sm text-slate-600">
                        {mode === "forgot" ? "Enter your email and we'll send a reset link." : "Access your Smarter Choice account"}
                    </p>
                </DialogHeader>

                {mode === "forgot" && sent ? (
                    <div className="py-4 text-center" data-testid="forgot-sent">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal/10"><MailCheck className="h-7 w-7 text-teal" /></div>
                        <p className="text-sm text-slate-600">If an account exists for that email, we've sent a link to reset your password. The link expires in 1 hour.</p>
                        <Button onClick={() => reset("login")} className="mt-6 w-full bg-navy text-white hover:bg-navy-light" data-testid="forgot-back-btn">Back to sign in</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4 pt-2">
                        {mode === "register" && (
                            <div className="space-y-1.5">
                                <Label htmlFor="auth-name">Full name</Label>
                                <Input id="auth-name" data-testid="auth-name-input" value={form.name} onChange={set("name")} required placeholder="Jordan Carter" />
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label htmlFor="auth-email">Email</Label>
                            <Input id="auth-email" data-testid="auth-email-input" type="email" autoComplete="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" />
                        </div>
                        {mode !== "forgot" && (
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="auth-password">Password</Label>
                                    {mode === "login" && (
                                        <button type="button" data-testid="auth-forgot-link" onClick={() => reset("forgot")} className="text-xs font-semibold text-ocean hover:underline">Forgot password?</button>
                                    )}
                                </div>
                                <Input id="auth-password" data-testid="auth-password-input" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={form.password} onChange={set("password")} required placeholder="••••••••" />
                            </div>
                        )}

                        {mode === "register" && (
                            <div className="flex items-start gap-2.5">
                                <Checkbox id="auth-accept" data-testid="auth-accept-terms" checked={accept} onCheckedChange={(v) => setAccept(!!v)} className="mt-0.5" />
                                <Label htmlFor="auth-accept" className="text-xs font-normal leading-relaxed text-slate-600">
                                    I agree to the <Link to="/legal/terms" target="_blank" className="font-semibold text-ocean hover:underline">Terms of Use</Link> and <Link to="/legal/privacy" target="_blank" className="font-semibold text-ocean hover:underline">Privacy Notice</Link>.
                                </Label>
                            </div>
                        )}

                        {err && <p className="text-sm text-red-500" data-testid="auth-error">{err}</p>}
                        <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="h-11 w-full bg-navy font-semibold text-white hover:bg-navy-light">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
                        </Button>

                        <p className="text-center text-sm text-slate-600">
                            {mode === "login" && (<>New here? <button type="button" data-testid="auth-toggle-mode" onClick={() => reset("register")} className="font-semibold text-ocean hover:underline">Create an account</button></>)}
                            {mode === "register" && (<>Already have an account? <button type="button" data-testid="auth-toggle-mode" onClick={() => reset("login")} className="font-semibold text-ocean hover:underline">Sign in</button></>)}
                            {mode === "forgot" && (<button type="button" data-testid="auth-toggle-mode" onClick={() => reset("login")} className="font-semibold text-ocean hover:underline">Back to sign in</button>)}
                        </p>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
